/**
 * Guest Wishlist Card Button Component
 * Custom element for adding/removing products from guest wishlists on product cards
 * Displays Plus icon with wishlist state (same UI as logged-in users)
 */

class GuestWishlistCardButton extends HTMLElement {
  connectedCallback() {
    this.setupEventListeners();
    this.handleManagerAvailability();
    window.addEventListener('guestWishlistManagerReady', this.handleManagerAvailability);
  }

  disconnectedCallback() {
    window.removeEventListener('guestWishlistManagerReady', this.handleManagerAvailability);
    this.unsubscribeWishlistChanges?.();
  }

  handleManagerAvailability = () => {
    const button = this.querySelector('.card__project-list-tag');
    if (!window.guestWishlistManager) {
      button.disabled = true;
      return;
    }

    button.disabled = false;
    this.updateState();
    if (!this.unsubscribeWishlistChanges) {
      this.unsubscribeWishlistChanges = window.guestWishlistManager.onWishlistChange(() => {
        this.updateState();
      });
    }
  }

  getProductId() {
    return this.getAttribute('data-product-id');
  }

  getVariantId() {
    return this.getAttribute('data-variant-id') || this.getAttribute('data-product-id');
  }

  getProductTitle() {
    return this.getAttribute('data-product-title') || 'Product';
  }

  getProductPrice() {
    return parseFloat(this.getAttribute('data-product-price')) || 0;
  }

  getProductImage() {
    return this.getAttribute('data-product-image') || '';
  }

  getProductHandle() {
    return this.getAttribute('data-product-handle') || '';
  }

  setupEventListeners() {
    const button = this.querySelector('.card__project-list-tag');

    button.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      if (!window.guestWishlistManager) {
        console.warn('Guest wishlist manager not available');
        return;
      }

      this.openProjectSelector();
    });
  }

  openProjectSelector() {
    window.dispatchEvent(new CustomEvent('guestProjectSelectionRequest', {
      detail: {
        item: {
          productId: this.getProductId(),
          variantId: this.getVariantId(),
          title: this.getProductTitle(),
          price: this.getProductPrice(),
          image: this.getProductImage(),
          handle: this.getProductHandle()
        }
      }
    }));
  }

  addToWishlist() {
    if (!window.guestWishlistManager) return;

    // Get or create default wishlist
    let wishlists = window.guestWishlistManager.getAllWishlists();
    let defaultWishlist = wishlists.find(w => w.name === 'My Wishlist' && w.isGuest);

    if (!defaultWishlist) {
      defaultWishlist = window.guestWishlistManager.createWishlist('My Wishlist');
    }

    const item = {
      productId: this.getProductId(),
      variantId: this.getVariantId(),
      title: this.getProductTitle(),
      price: this.getProductPrice(),
      image: this.getProductImage(),
      handle: this.getProductHandle()
    };

    window.guestWishlistManager.addItemToWishlist(defaultWishlist.id, item);
    this.showNotification('Added to wishlist!');
  }

  removeFromWishlist() {
    if (!window.guestWishlistManager) return;

    const productId = this.getProductId();
    const variantId = this.getVariantId();
    const wishlists = window.guestWishlistManager.getWishlistsContainingItem(productId, variantId);

    wishlists.forEach(wishlist => {
      const item = wishlist.items.find(
        i => i.productId == productId && i.variantId == variantId
      );
      if (item) {
        window.guestWishlistManager.removeItemFromWishlist(wishlist.id, item.id);
      }
    });

    this.showNotification('Removed from wishlist');
  }

  updateState() {
    if (!window.guestWishlistManager) return;

    const productId = this.getProductId();
    const variantId = this.getVariantId();
    const button = this.querySelector('.card__project-list-tag');
    const isInWishlist = window.guestWishlistManager.isItemInWishlist(productId, variantId);

    if (isInWishlist) {
      button.classList.add('is-in-wishlist');
    } else {
      button.classList.remove('is-in-wishlist');
    }
  }

  showNotification(message) {
    // Dispatch custom event for notification
    const event = new CustomEvent('wishlistNotification', {
      detail: { message }
    });
    window.dispatchEvent(event);
  }
}

customElements.define('guest-wishlist-card-button', GuestWishlistCardButton);
